// frontend/src/components/PaperModePanel.js
// ----------------------------------------------------
// ✅ Redesigned Paper Mode Dashboard - Matches Live Mode UI
// - Modern card-based design
// - Stats grid, controls, positions, trade history
// ----------------------------------------------------

import React, { useEffect, useState, useMemo, useCallback, useRef } from "react";
import axios from "axios";
import { API_BASE } from "../config";

export default function PaperModePanel() {
  const [running, setRunning] = useState(false);
  const [mode, setMode] = useState("paper_live");
  const [loading, setLoading] = useState(false);
  const [state, setState] = useState(null);
  const [error, setError] = useState("");
  const [isLiveRunning, setIsLiveRunning] = useState(false);
  const [lastTickTime, setLastTickTime] = useState(null);

  // Trade data
  const [trades, setTrades] = useState([]);
  const [positions, setPositions] = useState([]);
  const [summary, setSummary] = useState({
    cash: 1000000,
    equity: 1000000,
    realizedPnL: 0,
    unrealizedPnL: 0,
  });
  const [marketData, setMarketData] = useState({
    symbol: "NIFTY",
    spot: 0,
    bias: "NEUTRAL",
    indexRsi: 0,
    itm: null,
  });

  // 🔁 Check scheduler status
  useEffect(() => {
    const checkStatus = async () => {
      try {
        const { data } = await axios.get(`${API_BASE}/strategy/status`);
        if (data.ok) {
          // Paper and Live run independently now
          // Check if paper scheduler is running
          const isPaperRunning = data.schedulerRunning &&
            (data.schedulerMode === "paper" ||
              data.schedulerMode === "paper_live" ||
              data.schedulerMode === "both");
          setRunning(isPaperRunning);

          // Live running status (for info only, doesn't block paper)
          setIsLiveRunning(data.schedulerMode === "live" || data.schedulerMode === "both");
        }
      } catch (err) {
        console.warn("⚠️ Could not fetch scheduler status:", err.message);
      }
    };

    checkStatus();
    const interval = setInterval(checkStatus, 5000);
    return () => clearInterval(interval);
  }, []);

  // 🔁 Fetch Paper State + Trade Data
  const fetchAll = useCallback(async () => {
    try {
      const [stateRes, historyRes, posRes, sumRes] = await Promise.all([
        axios.get(`${API_BASE}/paper/state`),
        axios.get(`${API_BASE}/trades/history`),
        axios.get(`${API_BASE}/trades/positions`),
        axios.get(`${API_BASE}/trades/summary`),
      ]);

      if (stateRes.data?.ok && stateRes.data.state) {
        setState(stateRes.data.state);
      }
      if (historyRes.data?.ok) {
        setTrades([...historyRes.data.trades].reverse());
      }
      if (posRes.data?.ok) {
        setPositions(posRes.data.positions || []);
      }
      if (sumRes.data?.ok) {
        setSummary(sumRes.data.summary || {});
      }

      setLastTickTime(Date.now());
      setError("");
    } catch (err) {
      setError("Network Error: Backend unreachable");
      console.error("❌ Fetch error:", err.message);
    }
  }, []);

  useEffect(() => {
    fetchAll();
    const id = setInterval(fetchAll, 5000);
    return () => clearInterval(id);
  }, [fetchAll]);

  // 📊 Fetch Market Data (same endpoint as Live Mode)
  useEffect(() => {
    let cancelled = false;
    const fetchMarketData = async () => {
      try {
        const { data } = await axios.get(`${API_BASE}/strategy/market-data`);
        if (!cancelled && data?.ok) {
          setMarketData({
            symbol: data.symbol || "NIFTY",
            spot: data.spot || 0,
            bias: data.bias || "NEUTRAL",
            indexRsi: data.indexRsi || 0,
            itm: data.itm || null,
          });
        }
      } catch (err) {
        // silent - market data is optional
      }
    };

    fetchMarketData();
    const interval = setInterval(fetchMarketData, 5000);
    return () => { cancelled = true; clearInterval(interval); };
  }, []);

  // ▶️ Start Scheduler
  const startEngine = async () => {
    try {
      setLoading(true);
      const { data } = await axios.post(`${API_BASE}/strategy/start`, { mode });
      if (data?.ok) {
        console.log("✅ Paper scheduler started:", data.message);
        setRunning(true);
        await fetchAll();
      } else {
        alert("Failed to start scheduler: " + (data?.message || "Unknown error"));
      }
    } catch (err) {
      console.error("❌ Error starting scheduler:", err.message);
      alert("Error: " + err.message);
    } finally {
      setLoading(false);
    }
  };

  // ⏹️ Stop Scheduler
  const stopEngine = async () => {
    if (!window.confirm("Are you sure you want to stop the paper scheduler?")) return;

    try {
      setLoading(true);
      const { data } = await axios.get(`${API_BASE}/strategy/status`);
      let currentMode = data.schedulerMode || mode;
      if (currentMode === "both") currentMode = "paper"; // Stop paper component only

      const resp = await axios.post(`${API_BASE}/strategy/stop`, { mode: currentMode });
      if (resp?.data?.ok) {
        console.log(`🛑 ${currentMode} scheduler stopped.`);
        setRunning(false);
        await fetchAll();
      }
    } catch (err) {
      console.error("❌ Error stopping scheduler:", err.message);
    } finally {
      setLoading(false);
    }
  };

  // Exit position
  const handleExit = async (pos) => {
    const confirmExit = window.confirm(
      `Exit ${pos.tradingsymbol || pos.symbol} (${Math.abs(pos.qty)} qty)?`
    );
    if (!confirmExit) return;

    try {
      const token = pos.token || pos.instrument_token;
      const response = await axios.post(`${API_BASE}/trades/close/${token}`, {});

      if (response.data?.ok) {
        alert(`✅ ${pos.tradingsymbol || pos.symbol} exited successfully.`);
        await fetchAll();
      } else {
        alert("❌ Exit failed: " + response.data?.error);
      }
    } catch (err) {
      console.error("❌ Error exiting position:", err.message);
      alert("Exit failed: " + (err.response?.data?.error || err.message));
    }
  };

  // Clear trades
  const clearTrades = async () => {
    if (!window.confirm("Clear all recorded trades?")) return;
    try {
      await axios.delete(`${API_BASE}/trades/clear`);
      setTrades([]);
    } catch (err) {
      console.error("❌ Error clearing trades:", err.message);
    }
  };

  // Calculate totals
  const realizedPnL = summary.realizedPnL || 0;
  const unrealizedPnL = summary.unrealizedPnL || 0;
  const totalPnL = realizedPnL + unrealizedPnL;
  const cash = summary.cash || state?.cash || 1000000;
  const equity = summary.equity || state?.paperBrokerEquity || 1000000;

  // Loading state
  if (!state && !error) {
    return (
      <div className="paper-dashboard">
        <div className="loading-state">
          <div className="loading-spinner"></div>
          <p>Loading Paper Mode...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="paper-dashboard">
      {/* Live Mode Info - Both can run simultaneously */}
      {isLiveRunning && (
        <div className="info-banner">
          <span>ℹ️</span>
          <span>Live trading is also running. Paper mode operates independently.</span>
        </div>
      )}

      {/* Stats Grid */}
      <div className="stats-grid">
        <div className="stat-card">
          <div className="stat-label">Cash Balance</div>
          <div className="stat-value">₹{cash.toLocaleString("en-IN")}</div>
        </div>
        <div className="stat-card">
          <div className="stat-label">Equity</div>
          <div className="stat-value">₹{equity.toLocaleString("en-IN")}</div>
        </div>
        <div className="stat-card">
          <div className="stat-label">Realized P&L</div>
          <div className={`stat-value ${realizedPnL >= 0 ? 'positive' : 'negative'}`}>
            {realizedPnL >= 0 ? '+' : ''}₹{realizedPnL.toFixed(2)}
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-label">Total P&L</div>
          <div className={`stat-value ${totalPnL >= 0 ? 'positive' : 'negative'}`}>
            {totalPnL >= 0 ? '+' : ''}₹{totalPnL.toFixed(2)}
          </div>
        </div>
      </div>

      {/* Status & Controls */}
      <div className="dashboard-controls">
        <div className="status-display">
          <span className={`status-dot ${running ? 'online' : 'offline'}`}></span>
          <span className={running ? 'text-success' : 'text-danger'}>
            {running ? `Running (${mode === 'paper_live' ? 'Paper-Live' : 'Paper'})` : 'Stopped'}
          </span>
        </div>

        <div className="control-group">
          <select
            value={mode}
            onChange={(e) => setMode(e.target.value)}
            disabled={running}
            className="mode-select"
          >
            <option value="paper">Paper (Offline)</option>
            <option value="paper_live">Paper-Live (Real Ticks)</option>
          </select>

          <div className="control-buttons">
            <button
              onClick={startEngine}
              disabled={running || loading}
              className={`btn ${running ? 'btn-outline' : 'btn-success'}`}
            >
              <span>▶️</span> {loading && !running ? 'Starting...' : 'Start'}
            </button>
            <button
              onClick={stopEngine}
              disabled={!running || loading}
              className={`btn ${running ? 'btn-danger' : 'btn-outline'}`}
            >
              <span>⏹</span> {loading && running ? 'Stopping...' : 'Stop'}
            </button>
          </div>
        </div>
      </div>

      {/* Error Display */}
      {error && (
        <div className="error-banner">
          <span>⚠️</span>
          <span>{error}</span>
        </div>
      )}

      {/* Cards Row: Positions + Empty Card */}
      <div className="cards-row">
        <ActivePositionsCard
          positions={positions}
          handleExit={handleExit}
        />
        <PaperMarketCard marketData={marketData} />
      </div>

      {/* Trade History Table */}
      <TradeHistorySection
        trades={trades}
        onClear={clearTrades}
        onRefresh={fetchAll}
      />

      <style>{`
        .paper-dashboard {
          color: var(--color-text-primary);
        }

        .loading-state {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          padding: 60px;
          color: var(--color-text-secondary);
        }

        .warning-banner {
          display: flex;
          align-items: center;
          gap: 10px;
          padding: 12px 16px;
          background: rgba(250, 204, 21, 0.15);
          border: 1px solid rgba(250, 204, 21, 0.3);
          border-radius: var(--radius-md);
          margin-bottom: 20px;
          color: #facc15;
          font-weight: 500;
        }

        .info-banner {
          display: flex;
          align-items: center;
          gap: 10px;
          padding: 12px 16px;
          background: rgba(59, 130, 246, 0.15);
          border: 1px solid rgba(59, 130, 246, 0.3);
          border-radius: var(--radius-md);
          margin-bottom: 20px;
          color: #60a5fa;
          font-weight: 500;
        }

        .error-banner {
          display: flex;
          align-items: center;
          gap: 10px;
          padding: 12px 16px;
          background: var(--color-danger-bg);
          border: 1px solid rgba(239, 68, 68, 0.3);
          border-radius: var(--radius-md);
          margin-bottom: 20px;
          color: var(--color-danger-light);
        }

        .dashboard-controls {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 16px 20px;
          background: rgba(255, 255, 255, 0.03);
          border: 1px solid var(--color-border);
          border-radius: var(--radius-md);
          margin-bottom: 24px;
        }

        .status-display {
          display: flex;
          align-items: center;
          gap: 10px;
          font-weight: 500;
        }

        .control-group {
          display: flex;
          align-items: center;
          gap: 16px;
        }

        .mode-select {
          padding: 8px 12px;
          background: var(--color-bg-elevated);
          border: 1px solid var(--color-border);
          border-radius: var(--radius-sm);
          color: var(--color-text-primary);
          font-size: 0.9rem;
          cursor: pointer;
        }

        .mode-select:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }

        .control-buttons {
          display: flex;
          gap: 10px;
        }

        .cards-row {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 24px;
          margin-bottom: 32px;
        }

        @media (max-width: 900px) {
          .cards-row {
            grid-template-columns: 1fr;
          }

          .dashboard-controls {
            flex-direction: column;
            gap: 16px;
          }

          .control-group {
            flex-direction: column;
            width: 100%;
          }

          .mode-select {
            width: 100%;
          }

          .control-buttons {
            width: 100%;
          }

          .control-buttons .btn {
            flex: 1;
          }
        }
      `}</style>
    </div>
  );
}


// ----------------------------------------------------
// 📦 Active Positions Card
// ----------------------------------------------------
function ActivePositionsCard({ positions, handleExit }) {
  const hasPositions = positions && positions.length > 0;

  return (
    <div className="positions-card">
      <div className="card-header">
        <span className="card-icon">📦</span>
        <h3>Open Positions</h3>
        {hasPositions && <span className="position-count">{positions.length}</span>}
      </div>

      {!hasPositions ? (
        <div className="empty-positions">
          <div className="empty-icon-container">
            <div className="empty-icon-ring"></div>
            <span className="empty-icon">🔍</span>
          </div>
          <p>No open positions</p>
        </div>
      ) : (
        <div className="positions-list">
          {positions.map((pos, i) => {
            const pnl = pos.unrealized || 0;
            const isProfit = pnl >= 0;
            return (
              <div key={pos.token || pos.instrument_token || i} className={`position-item ${isProfit ? 'profit' : 'loss'}`}>
                <div className="position-header">
                  <div className="position-symbol">{pos.tradingsymbol || pos.symbol}</div>
                  <span className={`position-side ${pos.qty > 0 ? 'buy' : 'sell'}`}>
                    {pos.qty > 0 ? 'LONG' : 'SHORT'}
                  </span>
                </div>

                <div className="position-stats">
                  <div className="stat-item">
                    <span className="stat-label">Qty</span>
                    <span className="stat-value">{Math.abs(pos.qty)}</span>
                  </div>
                  <div className="stat-item">
                    <span className="stat-label">Entry</span>
                    <span className="stat-value">₹{pos.avg?.toFixed(2) || '-'}</span>
                  </div>
                  <div className="stat-item">
                    <span className="stat-label">LTP</span>
                    <span className="stat-value">₹{pos.lastPx?.toFixed(2) || '-'}</span>
                  </div>
                </div>

                <div className="position-pnl-container">
                  <span className={`position-pnl ${isProfit ? 'positive' : 'negative'}`}>
                    {isProfit ? '+' : ''}₹{pnl.toFixed(2)}
                  </span>
                </div>

                <button
                  onClick={() => handleExit(pos)}
                  className="exit-btn"
                >
                  Exit
                </button>
              </div>
            );
          })}
        </div>
      )}

      <style>{`
        .positions-card {
          background: linear-gradient(145deg, rgba(30, 32, 45, 0.95) 0%, rgba(20, 22, 35, 0.98) 100%);
          border: 1px solid rgba(255, 255, 255, 0.08);
          border-radius: 16px;
          padding: 24px;
          min-height: 280px;
          box-shadow: 0 8px 32px rgba(0, 0, 0, 0.3);
        }

        .card-header {
          display: flex;
          align-items: center;
          gap: 12px;
          margin-bottom: 20px;
          padding-bottom: 16px;
          border-bottom: 1px solid rgba(255, 255, 255, 0.06);
        }

        .card-header h3 {
          margin: 0;
          font-size: 1.1rem;
          font-weight: 600;
          color: #fff;
          flex: 1;
        }

        .card-icon {
          font-size: 1.3rem;
        }

        .position-count {
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          color: white;
          font-size: 0.75rem;
          font-weight: 700;
          padding: 4px 10px;
          border-radius: 12px;
        }

        .empty-positions {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          text-align: center;
          padding: 50px 20px;
        }

        .empty-icon-container {
          position: relative;
          margin-bottom: 20px;
        }

        .empty-icon-ring {
          position: absolute;
          width: 80px;
          height: 80px;
          border-radius: 50%;
          border: 2px solid rgba(102, 126, 234, 0.3);
          top: 50%;
          left: 50%;
          transform: translate(-50%, -50%);
          animation: pulse-ring 2s ease-out infinite;
        }

        @keyframes pulse-ring {
          0% { transform: translate(-50%, -50%) scale(0.8); opacity: 1; }
          100% { transform: translate(-50%, -50%) scale(1.3); opacity: 0; }
        }

        .empty-icon {
          font-size: 2.5rem;
          display: block;
          position: relative;
          z-index: 1;
        }

        .empty-positions p {
          font-size: 1rem;
          color: rgba(255, 255, 255, 0.5);
          margin: 0;
        }

        .positions-list {
          display: flex;
          flex-direction: column;
          gap: 16px;
        }

        .position-item {
          background: linear-gradient(135deg, rgba(40, 44, 60, 0.6) 0%, rgba(30, 34, 50, 0.8) 100%);
          border-radius: 12px;
          padding: 20px;
          border: 1px solid rgba(255, 255, 255, 0.06);
          transition: all 0.3s ease;
        }

        .position-item:hover {
          transform: translateY(-2px);
          box-shadow: 0 8px 25px rgba(0, 0, 0, 0.3);
        }

        .position-item.profit {
          border-left: 3px solid #10b981;
        }

        .position-item.loss {
          border-left: 3px solid #ef4444;
        }

        .position-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 16px;
        }

        .position-symbol {
          font-weight: 700;
          font-size: 1rem;
          color: #fff;
          font-family: 'SF Mono', 'Consolas', monospace;
          letter-spacing: 0.5px;
        }

        .position-side {
          padding: 5px 12px;
          border-radius: 6px;
          font-weight: 700;
          font-size: 0.75rem;
          text-transform: uppercase;
          letter-spacing: 0.5px;
        }

        .position-side.buy {
          background: linear-gradient(135deg, rgba(16, 185, 129, 0.2) 0%, rgba(16, 185, 129, 0.1) 100%);
          color: #34d399;
          border: 1px solid rgba(16, 185, 129, 0.3);
        }

        .position-side.sell {
          background: linear-gradient(135deg, rgba(239, 68, 68, 0.2) 0%, rgba(239, 68, 68, 0.1) 100%);
          color: #f87171;
          border: 1px solid rgba(239, 68, 68, 0.3);
        }

        .position-stats {
          display: flex;
          gap: 24px;
          margin-bottom: 16px;
        }

        .stat-item {
          display: flex;
          flex-direction: column;
          gap: 4px;
        }

        .stat-label {
          font-size: 0.7rem;
          color: rgba(255, 255, 255, 0.4);
          text-transform: uppercase;
          letter-spacing: 1px;
        }

        .stat-value {
          font-size: 0.95rem;
          color: #fff;
          font-weight: 600;
          font-family: 'SF Mono', 'Consolas', monospace;
        }

        .position-pnl-container {
          display: flex;
          align-items: center;
          justify-content: flex-start;
          margin-bottom: 16px;
          padding: 12px 16px;
          background: rgba(0, 0, 0, 0.2);
          border-radius: 8px;
        }

        .position-pnl {
          font-family: 'SF Mono', 'Consolas', monospace;
          font-weight: 700;
          font-size: 1.25rem;
        }

        .position-pnl.positive {
          color: #10b981;
          text-shadow: 0 0 20px rgba(16, 185, 129, 0.4);
        }

        .position-pnl.negative {
          color: #ef4444;
          text-shadow: 0 0 20px rgba(239, 68, 68, 0.4);
        }

        .exit-btn {
          width: 100%;
          padding: 12px;
          background: linear-gradient(135deg, #ef4444 0%, #dc2626 100%);
          color: white;
          border: none;
          border-radius: 8px;
          font-weight: 600;
          font-size: 0.9rem;
          cursor: pointer;
          transition: all 0.2s ease;
          text-transform: uppercase;
          letter-spacing: 0.5px;
        }

        .exit-btn:hover {
          background: linear-gradient(135deg, #dc2626 0%, #b91c1c 100%);
          transform: translateY(-1px);
          box-shadow: 0 4px 15px rgba(239, 68, 68, 0.4);
        }

        .exit-btn:active {
          transform: translateY(0);
        }
      `}</style>
    </div>
  );
}


// ----------------------------------------------------
// 📊 Paper Market Card (matches Live Mode Current Market)
// ----------------------------------------------------
function PaperMarketCard({ marketData }) {
  const isCall = marketData.bias === "CALL";
  const isPut = marketData.bias === "PUT";
  const isNeutral = !isCall && !isPut;
  const activeItm = marketData.itm;

  return (
    <div className="p-market-card">
      <div className="card-header">
        <span className="card-icon">📊</span>
        <h3>Current Market</h3>
      </div>

      <div className="p-market-inner">
        <div className="p-market-top">
          <div className="p-market-symbol">{marketData.symbol || "NIFTY"}</div>
          <span className={`p-bias-badge ${isPut ? 'bearish' : isCall ? 'bullish' : 'neutral'}`}>
            {isCall ? "📈 CALL" : isPut ? "📉 PUT" : "⏸️ NEUTRAL"}
          </span>
        </div>

        <div className="p-spot-row">
          <span className="p-spot-label">Spot</span>
          <span className="p-spot-value">₹{(marketData.spot || 0).toLocaleString('en-IN', { minimumFractionDigits: 1 })}</span>
        </div>

        <div className="p-ema-row">
          <div className="p-ema-item">
            <span className="p-ema-label">Index RSI</span>
            <span className={`p-ema-value ${(marketData.indexRsi || 0) > 60 ? 'rsi-high' : (marketData.indexRsi || 0) < 40 ? 'rsi-low' : ''}`}>
              {(marketData.indexRsi || 0).toFixed(1)}
            </span>
          </div>

          {activeItm ? (
            <>
              <div className="p-ema-item">
                <span className="p-ema-label">ITM {activeItm.type}</span>
                <span className={`p-ema-value ${activeItm.type === 'CE' ? 'itm-ce' : 'itm-pe'}`}>
                  {activeItm.strike}
                  {activeItm.premium && (
                    <span className="p-itm-premium">₹{activeItm.premium.toFixed(2)}</span>
                  )}
                </span>
              </div>
              <div className="p-ema-item">
                <span className="p-ema-label">ITM RSI</span>
                <span className={`p-ema-value ${(activeItm.rsi || 0) > 60 ? 'rsi-high' : (activeItm.rsi || 0) < 40 ? 'rsi-low' : ''}`}>
                  {(activeItm.rsi || 0).toFixed(1)}
                </span>
              </div>
            </>
          ) : (
            <>
              <div className="p-ema-item">
                <span className="p-ema-label">ITM</span>
                <span className="p-ema-value" style={{ color: 'rgba(255,255,255,0.3)' }}>
                  Waiting for trade...
                </span>
              </div>
              <div className="p-ema-item">
                <span className="p-ema-label">ITM RSI</span>
                <span className="p-ema-value" style={{ color: 'rgba(255,255,255,0.3)' }}>
                  -
                </span>
              </div>
            </>
          )}
        </div>
      </div>

      <style>{`
        .p-market-card {
          background: linear-gradient(145deg, rgba(30, 32, 45, 0.95) 0%, rgba(20, 22, 35, 0.98) 100%);
          border: 1px solid rgba(255, 255, 255, 0.08);
          border-radius: 16px;
          padding: 24px;
          box-shadow: 0 8px 32px rgba(0, 0, 0, 0.3);
        }

        .p-market-inner {
          background: rgba(0, 0, 0, 0.3);
          border: 1px solid rgba(255, 255, 255, 0.06);
          border-radius: 12px;
          padding: 20px;
        }

        .p-market-top {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 12px;
        }

        .p-market-symbol {
          font-size: 1.15rem;
          font-weight: 700;
          color: #fff;
          letter-spacing: 0.5px;
        }

        .p-bias-badge {
          padding: 5px 14px;
          border-radius: 20px;
          font-size: 0.75rem;
          font-weight: 700;
          text-transform: uppercase;
          letter-spacing: 0.5px;
        }

        .p-bias-badge.bullish {
          background: linear-gradient(135deg, rgba(16, 185, 129, 0.2) 0%, rgba(16, 185, 129, 0.1) 100%);
          color: #34d399;
          border: 1px solid rgba(16, 185, 129, 0.3);
        }

        .p-bias-badge.bearish {
          background: linear-gradient(135deg, rgba(239, 68, 68, 0.2) 0%, rgba(239, 68, 68, 0.1) 100%);
          color: #f87171;
          border: 1px solid rgba(239, 68, 68, 0.3);
        }

        .p-bias-badge.neutral {
          background: rgba(255, 255, 255, 0.08);
          color: rgba(255, 255, 255, 0.6);
          border: 1px solid rgba(255, 255, 255, 0.1);
        }

        .p-spot-row {
          display: flex;
          justify-content: space-between;
          align-items: baseline;
          margin-bottom: 20px;
        }

        .p-spot-label {
          color: rgba(255, 255, 255, 0.4);
          font-size: 0.85rem;
        }

        .p-spot-value {
          font-size: 1.6rem;
          font-weight: 700;
          font-family: 'SF Mono', 'Consolas', monospace;
          color: #fff;
        }

        .p-ema-row {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 12px;
          padding: 16px 0 4px;
          border-top: 1px solid rgba(255, 255, 255, 0.06);
        }

        .p-ema-item {
          text-align: center;
        }

        .p-ema-label {
          display: block;
          font-size: 0.7rem;
          color: rgba(255, 255, 255, 0.4);
          margin-bottom: 6px;
          text-transform: uppercase;
          letter-spacing: 1px;
        }

        .p-ema-value {
          font-family: 'SF Mono', 'Consolas', monospace;
          font-weight: 600;
          color: rgba(255, 255, 255, 0.8);
          font-size: 0.95rem;
        }

        .p-ema-value.rsi-high {
          color: #f87171;
        }

        .p-ema-value.rsi-low {
          color: #34d399;
        }

        .p-ema-value.itm-ce {
          color: #34d399;
        }

        .p-ema-value.itm-pe {
          color: #f87171;
        }

        .p-itm-premium {
          display: block;
          font-size: 0.8rem;
          color: rgba(255, 255, 255, 0.4);
          margin-top: 2px;
        }
      `}</style>
    </div>
  );
}


// ----------------------------------------------------
// 📜 Trade History Section
// ----------------------------------------------------
function TradeHistorySection({ trades, onClear, onRefresh }) {
  // Group trades by date
  const groupedTrades = useMemo(() => {
    if (!trades || trades.length === 0) return [];

    return trades.map(t => {
      const date = new Date(t.ts || t.entryTime);
      const dateStr = date.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
      const timeStr = date.toLocaleTimeString('en-IN', { hour12: false });
      const pnl = t.pnl || 0;
      const type = (t.tradingsymbol || t.symbol || '').includes('CE') ? 'CE' :
        (t.tradingsymbol || t.symbol || '').includes('PE') ? 'PE' : '-';

      return {
        ...t,
        dateStr,
        timeStr,
        pnl,
        type
      };
    });
  }, [trades]);

  if (groupedTrades.length === 0) {
    return (
      <div className="history-section">
        <div className="section-header">
          <h4 className="section-title">
            <span>📜</span> Trade History
          </h4>
          <div className="section-actions">
            <button onClick={onRefresh} className="btn btn-outline btn-sm">
              🔄 Refresh
            </button>
          </div>
        </div>
        <div className="empty-state">
          <span className="empty-icon">📭</span>
          <p>No trades recorded yet.</p>
        </div>

        <style>{`
          .history-section {
            margin-bottom: 32px;
          }

          .section-header {
            display: flex;
            justify-content: space-between;
            align-items: center;
            margin-bottom: 16px;
            padding-bottom: 12px;
            border-bottom: 1px solid var(--color-border);
          }

          .section-title {
            display: flex;
            align-items: center;
            gap: 8px;
            font-size: 1.1rem;
            font-weight: 600;
            color: var(--color-text-primary);
            margin: 0;
          }

          .section-actions {
            display: flex;
            gap: 8px;
          }

          .btn-sm {
            padding: 6px 12px;
            font-size: 0.85rem;
          }

          .empty-state {
            display: flex;
            flex-direction: column;
            align-items: center;
            justify-content: center;
            padding: 40px 20px;
            color: var(--color-text-muted);
            text-align: center;
          }

          .empty-icon {
            font-size: 2.5rem;
            margin-bottom: 12px;
            opacity: 0.6;
          }
        `}</style>
      </div>
    );
  }

  return (
    <div className="history-section">
      <div className="section-header">
        <h4 className="section-title">
          <span>📜</span> Trade History
        </h4>
        <div className="section-actions">
          <button onClick={onRefresh} className="btn btn-outline btn-sm">
            🔄 Refresh
          </button>
          <button onClick={onClear} className="btn btn-danger btn-sm">
            🗑 Clear
          </button>
        </div>
      </div>

      <div className="table-wrapper">
        <table className="data-table">
          <thead>
            <tr>
              <th>Date</th>
              <th>Time</th>
              <th>Symbol</th>
              <th>Type</th>
              <th>Side</th>
              <th>Qty</th>
              <th>Price</th>
              <th>P&L</th>
            </tr>
          </thead>
          <tbody>
            {groupedTrades.map((t, i) => (
              <tr key={t.id || i}>
                <td>{t.dateStr}</td>
                <td className="font-mono">{t.timeStr}</td>
                <td className="font-mono symbol-cell">{t.tradingsymbol || t.symbol}</td>
                <td className={t.type === 'CE' ? 'text-success' : t.type === 'PE' ? 'text-danger' : ''}>
                  {t.type}
                </td>
                <td className={((t.side === 'BUY' || t.type === 'ENTRY') ? 'text-success' : 'text-danger')}>
                  {t.side === 'BUY' || t.side === 'SELL' ? t.side : (t.type === 'ENTRY' ? 'BUY' : t.type === 'CLOSE' || t.type === 'EXIT' ? 'SELL' : t.type)}
                </td>
                <td>{t.qty}</td>
                <td className="font-mono">₹{t.price?.toFixed?.(2) || t.entryPrice?.toFixed?.(2) || '-'}</td>
                <td className={`font-mono pnl-cell ${t.pnl >= 0 ? 'positive' : 'negative'}`}>
                  {t.pnl >= 0 ? '+' : ''}₹{t.pnl.toFixed(2)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <style>{`
        .history-section {
          margin-bottom: 32px;
        }

        .section-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 16px;
          padding-bottom: 12px;
          border-bottom: 1px solid var(--color-border);
        }

        .section-title {
          display: flex;
          align-items: center;
          gap: 8px;
          font-size: 1.1rem;
          font-weight: 600;
          color: var(--color-text-primary);
          margin: 0;
        }

        .section-actions {
          display: flex;
          gap: 8px;
        }

        .btn-sm {
          padding: 6px 12px;
          font-size: 0.85rem;
        }

        .table-wrapper {
          overflow-x: auto;
          border-radius: var(--radius-md);
          border: 1px solid var(--color-border);
        }

        .symbol-cell {
          color: var(--color-accent);
          font-weight: 500;
        }

        .pnl-cell.positive {
          color: var(--color-success-light);
          font-weight: 600;
        }

        .pnl-cell.negative {
          color: var(--color-danger-light);
          font-weight: 600;
        }
      `}</style>
    </div>
  );
}
